import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  useTheme,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import isEmpty from 'lodash.isempty'

import FileUploader from 'molecules/FileUploader/FileUploader'
import ResourceEditDialog from 'molecules/ResourceEditDialog'
import { isEditableContentType } from 'common/helpers/contentType'
import { useEffect, useState } from 'react'
import getID from 'common/helpers/getID'
import {
  validateDataModelSchemaFile,
  listDataModelSchemaResources,
  createDataModelSchemaResource,
  deleteDataModelSchemaResource,
  previewDataModelSchemaResource,
  downloadDataModelSchemaResource,
} from 'services/kraken-core/data_model/dataModel.service'
import { toast } from 'react-toastify'
import { DATA_MODEL_SCHEMA_RESOURCE_KINDS } from 'common/types/kraken-core/DataModelSchemaResource'
import { Button } from '@loadsmart/loadsmart-ui'
import { ValidateDataModelSchemaFileResponse } from 'services/kraken-core/data_model/types'
import ValidationStatusTag, { ValidationStatus } from 'atoms/ValidationStatusTag'

// Helper function to determine content type based on file extension
const getContentTypeFromFile = (file: File): string => {
  const extension = file.name.split('.').pop()?.toLowerCase()

  // Map file extensions to content types
  const extensionToContentType: Record<string, string> = {
    x12: 'application/EDI-X12',
    json: 'application/json',
    xml: 'application/xml',
    csv: 'text/csv',
    txt: 'text/plain',
    yaml: 'application/yaml',
    yml: 'application/yaml',
    md: 'text/markdown',
    pdf: 'application/pdf',
  }

  // If we have a mapping for this extension, use it
  if (extension && extensionToContentType[extension]) {
    return extensionToContentType[extension]
  }

  // Otherwise, use the file's type or default to octet-stream
  return file.type || 'application/octet-stream'
}

export interface DataModelSchemaValidationProps {
  dataModelSchemaId: string
  value?: string
  onChange: (changed: string) => void
  refreshTrigger?: number
  onResourceChange?: () => void
}

interface ValidationFile {
  id: string
  file: File
  status: ValidationStatus
  details?: string
  resourceId?: string
  name?: string
  content?: string
  contentType?: string
  content_type?: string
  validationResult?: ValidateDataModelSchemaFileResponse
}

interface ValidationResultsDialogProps {
  open: boolean
  onClose: () => void
  validationFile: ValidationFile | null
}

function ValidationResultsDialog({ open, onClose, validationFile }: ValidationResultsDialogProps) {
  const theme = useTheme()
  if (!validationFile) {
    return null
  }

  const fileName = validationFile.name || validationFile.file.name
  const { validationResult, status } = validationFile

  // Helper function to get the appropriate Tag for validation status
  const getValidationTag = () => {
    return <ValidationStatusTag status={status} showLoadingSpinner={false} />
  }

  // Helper function to render dialog content
  const renderDialogContent = () => {
    if (!validationResult) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ pt: 2 }}>
          {status === ValidationStatus.PENDING
            ? 'Validation has not been run yet. Click "Validate" to run validation on this file.'
            : status === ValidationStatus.RUNNING
            ? 'Validation is currently running...'
            : 'No validation results available.'}
        </Typography>
      )
    }

    return (
      <Stack spacing={2} pt={2}>
        {validationResult.message && (
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Message:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                backgroundColor: 'grey.100',
                fontFamily: 'monospace',
                padding: 2,
                borderRadius: 1,
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {validationResult.message}
            </Typography>
          </Box>
        )}

        {validationResult.details && validationResult.details.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Validation Details:
            </Typography>
            <Stack spacing={1}>
              {validationResult.details.map((detail: any, index: number) => (
                <Box
                  key={index}
                  sx={{
                    backgroundColor: 'grey.100',
                    padding: 2,
                    borderRadius: 1,
                  }}
                >
                  {detail.path && (
                    <Typography variant="body2" fontWeight="bold" gutterBottom>
                      Path: {detail.path}
                    </Typography>
                  )}
                  {detail.message && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                      }}
                    >
                      {detail.message}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Show full response for debugging if needed */}
        <Box>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Full Response:
          </Typography>
          <Typography
            variant="body2"
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              padding: 2,
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: '200px',
              overflow: 'auto',
            }}
          >
            {JSON.stringify(validationResult, null, 2)}
          </Typography>
        </Box>
      </Stack>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="h5" fontWeight="bold">
            Validation Results
          </Typography>
          {getValidationTag()}
        </Stack>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            mt: 1,
            opacity: 0.9,
          }}
        >
          {fileName}
        </Typography>
      </DialogTitle>
      <DialogContent>{renderDialogContent()}</DialogContent>
      <DialogActions>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function DataModelSchemaValidation(props: DataModelSchemaValidationProps) {
  const [files, setFiles] = useState<ValidationFile[]>([])
  const [selectedValidationFile, setSelectedValidationFile] = useState<ValidationFile>()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [previewContent, setPreviewContent] = useState<any>(null)
  const [isPreviewing, setIsPreviewing] = useState(false)
  const [validationDialogFile, setValidationDialogFile] = useState<ValidationFile | null>(null)
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false)

  const handleShowValidationResults = (file: ValidationFile) => {
    setValidationDialogFile(file)
    setIsValidationDialogOpen(true)
  }

  const handleEditResource = async (file: ValidationFile) => {
    if (!file.resourceId) return

    setSelectedValidationFile(file)

    // Only call preview endpoint for text-based content types
    const isEditable = isEditableContentType(file.content_type || file.contentType || '')

    if (isEditable) {
      setIsPreviewing(true)
      try {
        const { response } = await previewDataModelSchemaResource(file.resourceId)
        if (response?.data) {
          setPreviewContent(response.data)
          setSelectedValidationFile(prev => ({
            ...prev!,
            content: response.data.content,
            contentType: response.data.content_type,
          }))
        }
      } catch (error) {
        toast.error('Failed to load resource content')
        setSelectedValidationFile(undefined)
      } finally {
        setIsPreviewing(false)
      }
    } else {
      // For non-editable files, just use the existing file data
      setSelectedValidationFile(prev => ({
        ...prev!,
        content: '',
        contentType: file.content_type || file.contentType || '',
      }))
    }
  }

  const handleDeleteResource = (file: ValidationFile) => {
    setSelectedValidationFile(file)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!selectedValidationFile?.resourceId) return

    setIsDeleting(true)
    try {
      await deleteDataModelSchemaResource(selectedValidationFile.resourceId)
      toast.success('Resource deleted successfully')
      setIsDeleteDialogOpen(false)
      setSelectedValidationFile(undefined)

      // Refresh the list
      const response = await listDataModelSchemaResources({
        model_id: props.dataModelSchemaId,
      })

      const existingFiles = response.results
        .filter(resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION)
        .map(resource => ({
          id: getID(),
          file: new File([new Blob()], resource.name),
          status: ValidationStatus.PENDING,
          resourceId: resource.id,
          name: resource.name,
          content_type: resource.content_type,
        }))

      setFiles(existingFiles)
      // Notify parent component about the change
      if (props.onResourceChange) {
        props.onResourceChange()
      }
    } catch (error) {
      toast.error('Failed to delete resource')
    } finally {
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    const loadExistingValidationFiles = async () => {
      try {
        const response = await listDataModelSchemaResources({
          model_id: props.dataModelSchemaId,
        })

        const existingFiles = response.results
          .filter(resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION)
          .map(resource => ({
            id: getID(),
            file: new File([new Blob()], resource.name),
            status: ValidationStatus.PENDING,
            resourceId: resource.id,
            name: resource.name,
            content_type: resource.content_type,
          }))

        setFiles(existingFiles)
      } catch (error) {
        console.error('Failed to load validation resources:', error)
      }
    }

    loadExistingValidationFiles()
  }, [props.dataModelSchemaId, props.refreshTrigger])

  // Listen for refresh trigger from parent component
  useEffect(() => {
    if (props.refreshTrigger && props.refreshTrigger > 0) {
      const loadExistingValidationFiles = async () => {
        try {
          const response = await listDataModelSchemaResources({
            model_id: props.dataModelSchemaId,
          })

          const existingFiles = response.results
            .filter(resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION)
            .map(resource => ({
              id: getID(),
              file: new File([new Blob()], resource.name),
              status: ValidationStatus.PENDING,
              resourceId: resource.id,
              name: resource.name,
              content_type: resource.content_type,
            }))

          setFiles(existingFiles)
        } catch (error) {
          console.error('Failed to load validation resources:', error)
        }
      }
      loadExistingValidationFiles()
    }
  }, [props.refreshTrigger, props.dataModelSchemaId])

  const updateValidationFile = (newState: ValidationFile) => {
    setFiles(c => {
      const allExcept = c.filter(f => f.id != newState.id)
      const toBeUpdated = c.find(f => f.id === newState.id)
      if (!toBeUpdated) return c
      return [
        {
          ...newState,
        },
        ...allExcept,
      ]
    })
  }

  const handleFileUploaded = async (acceptedFiles: File[]) => {
    const uploadedFiles: ValidationFile[] = []

    for (const file of acceptedFiles) {
      try {
        const contentType = getContentTypeFromFile(file)
        const { response } = await createDataModelSchemaResource({
          model: props.dataModelSchemaId,
          name: file.name,
          file: file,
          content_type: contentType,
          kind: DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION,
        })

        if (response?.data) {
          uploadedFiles.push({
            id: getID(),
            file,
            status: ValidationStatus.PENDING,
            resourceId: response.data.id,
            name: response.data.name,
            content_type: response.data.content_type,
          })
        }
      } catch (error) {
        console.error('Failed to save validation resource:', error)
      }
    }

    // Refresh the list from server after upload
    const loadExistingValidationFiles = async () => {
      try {
        const response = await listDataModelSchemaResources({
          model_id: props.dataModelSchemaId,
        })

        const existingFiles = response.results
          .filter(resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION)
          .map(resource => ({
            id: getID(),
            file: new File([new Blob()], resource.name),
            status: ValidationStatus.PENDING,
            resourceId: resource.id,
            name: resource.name,
            content_type: resource.content_type,
          }))

        setFiles(existingFiles)

        // Notify parent component about the change
        if (props.onResourceChange) {
          props.onResourceChange()
        }
      } catch (error) {
        console.error('Failed to load validation resources:', error)
      }
    }

    loadExistingValidationFiles()
  }

  const handleRun = () => {
    async function validate(validationFile: ValidationFile) {
      updateValidationFile({
        ...validationFile,
        status: ValidationStatus.RUNNING,
      })

      try {
        let fileToValidate: File

        // If we have a resourceId, download the actual file content
        if (validationFile.resourceId) {
          const {
            response: downloadResponse,
            error: downloadError,
          } = await downloadDataModelSchemaResource(validationFile.resourceId)

          if (downloadError) {
            updateValidationFile({
              ...validationFile,
              status: ValidationStatus.FAILED,
              details: String(downloadError.response?.data || 'Failed to download file'),
              validationResult: {
                is_valid: false,
                message: String(
                  downloadError.response?.data?.message || 'Failed to download file for validation'
                ),
                details: downloadError.response?.data?.details || [],
              },
            })
            return
          }

          // Create a new File object with the downloaded content
          const fileName = validationFile.name || validationFile.file.name
          const contentType =
            validationFile.content_type || validationFile.contentType || 'application/octet-stream'
          fileToValidate = new File([downloadResponse!.data], fileName, { type: contentType })
        } else {
          // Use the original uploaded file
          fileToValidate = validationFile.file
        }

        const { response, error } = await validateDataModelSchemaFile({
          dataModelSchemaId: props.dataModelSchemaId,
          file: fileToValidate,
        })

        if (error) {
          updateValidationFile({
            ...validationFile,
            status: ValidationStatus.FAILED,
            details: String(error.response?.data),
            validationResult: {
              is_valid: false,
              message: String(error.response?.data?.message || 'Validation failed'),
              details: error.response?.data?.details || [],
            },
          })
          return
        }

        if (response)
          updateValidationFile({
            ...validationFile,
            status: response.data.is_valid ? ValidationStatus.SUCCESS : ValidationStatus.FAILED,
            details: response.data.details,
            validationResult: response.data,
          })
      } catch (error) {
        updateValidationFile({
          ...validationFile,
          status: ValidationStatus.FAILED,
          details: String(error),
          validationResult: {
            is_valid: false,
            message: String(error),
            details: [],
          },
        })
      }
    }

    files.forEach(f => {
      validate(f)
    })
  }

  return (
    <>
      <ValidationResultsDialog
        open={isValidationDialogOpen}
        onClose={() => {
          setIsValidationDialogOpen(false)
          setValidationDialogFile(null)
        }}
        validationFile={validationDialogFile}
      />

      {selectedValidationFile && selectedValidationFile.resourceId && (
        <ResourceEditDialog
          open={Boolean(selectedValidationFile)}
          onClose={() => {
            setSelectedValidationFile(undefined)
            setPreviewContent(null)
          }}
          resourceId={selectedValidationFile.resourceId}
          initialName={selectedValidationFile.name || selectedValidationFile.file.name}
          initialKind={DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION}
          content={selectedValidationFile.content || ''}
          contentType={selectedValidationFile.contentType || 'text/plain'}
          hideKindField={true}
          onSave={() => {
            // Refresh the list after saving
            const loadExistingValidationFiles = async () => {
              try {
                const response = await listDataModelSchemaResources({
                  model_id: props.dataModelSchemaId,
                })

                const existingFiles = response.results
                  .filter(resource => resource.kind === DATA_MODEL_SCHEMA_RESOURCE_KINDS.VALIDATION)
                  .map(resource => ({
                    id: getID(),
                    file: new File([new Blob()], resource.name),
                    status: ValidationStatus.PENDING,
                    resourceId: resource.id,
                    name: resource.name,
                    content_type: resource.content_type,
                  }))

                setFiles(existingFiles)
                // Notify parent component about the change
                if (props.onResourceChange) {
                  props.onResourceChange()
                }
              } catch (error) {
                console.error('Failed to load validation resources:', error)
              }
            }
            loadExistingValidationFiles()
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false)
          setSelectedValidationFile(undefined)
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Resource</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to delete{' '}
            {selectedValidationFile?.name || selectedValidationFile?.file.name}? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="secondary"
            onClick={() => {
              setIsDeleteDialogOpen(false)
              setSelectedValidationFile(undefined)
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" onClick={confirmDelete} loading={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Card>
        <CardHeader title="Validate sample files over the provided schema" />
        <CardContent>
          <Stack spacing={2}>
            <FileUploader data-testid="file-uploader" handleUpload={handleFileUploaded} />

            {!isEmpty(files) ? (
              <Stack spacing={2}>
                <Box display="flex" justifyContent="flex-end">
                  <Button color="primary" onClick={handleRun}>
                    Validate
                  </Button>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>File name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {files
                      .sort((a, b) =>
                        a.file.name
                          .toLocaleLowerCase()
                          .localeCompare(b.file.name.toLocaleLowerCase())
                      )
                      .map((f, index) => {
                        return (
                          <TableRow key={`file-row-${index}`}>
                            <TableCell>{f.name || f.file.name}</TableCell>
                            <TableCell width={'5%'}>
                              <ValidationStatusTag
                                status={f.status}
                                onClick={e => {
                                  e.stopPropagation()
                                  handleShowValidationResults(f)
                                }}
                                showFailureBadge={true}
                              />
                            </TableCell>
                            <TableCell width={'10%'}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {f.resourceId && (
                                  <>
                                    <Tooltip
                                      title={
                                        isEditableContentType(f.content_type || f.file.type)
                                          ? 'Edit resource'
                                          : 'Cannot edit binary/non-text files'
                                      }
                                    >
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={e => {
                                            e.stopPropagation()
                                            handleEditResource(f)
                                          }}
                                          disabled={
                                            !isEditableContentType(f.content_type || f.file.type)
                                          }
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                      <IconButton
                                        size="small"
                                        onClick={e => {
                                          e.stopPropagation()
                                          handleDeleteResource(f)
                                        }}
                                        disabled={isDeleting}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                  </TableBody>
                </Table>
              </Stack>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </>
  )
}
